import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Detect threats where identity lives',
    description:
      'Monosign watches the authentication and session layer itself, the place endpoint agents and network sensors never see clearly.',
    bullets: [
      'Detection catalog mapped to MITRE ATT&CK',
      'MFA-tampering detection (T1556) and session anomalies',
      'Incident console with SOC analytics and SLA tracking',
      'Per-detection tuning to fit your environment',
    ],
  },
  {
    title: 'Respond in seconds, automatically',
    description:
      'Detection without response is a report. Playbooks act on the identity fabric directly: the same platform that issued the session can kill it.',
    bullets: [
      'Revoke sessions and refresh tokens on incident',
      'Disable compromised accounts automatically',
      'Force step-up MFA on risky continuation',
      'Notify owners, managers, and the SOC',
    ],
  },
  {
    title: "Speak the industry's signal language",
    description:
      'Monosign implements the Shared Signals Framework and CAEP in both directions: it emits risk signals to your ecosystem and acts on the ones it receives.',
    bullets: [
      'SSF / CAEP transmitter and receiver',
      'Push and poll delivery with signed security event tokens',
      'Stream management: discovery, subjects, status',
      'Cross-vendor session revocation, standards-based',
    ],
  },
  {
    title: 'Feed your SOC, not another silo',
    description:
      'Every incident and risk signal flows to the tools your analysts already use, enriched with the identity context only the IdP has.',
    bullets: [
      'CEF export over Splunk HEC or Syslog TCP/TLS',
      'Incident CSV export for case work',
      'Dormant-access and peer-group outlier signals',
      'Risk-based auto-certification of low-risk access',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Enable detections',
    description:
      'Turn on the detection catalog and tune thresholds. Signals start flowing from every authentication and session event.',
  },
  {
    title: 'Wire the signals',
    description:
      'Connect SSF/CAEP peers and your SIEM. Monosign transmits what it sees and subscribes to what your ecosystem reports.',
  },
  {
    title: 'Automate response',
    description:
      'Attach playbooks to detections (revoke, disable, step-up, notify) and measure response against SLAs.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Security Score',
    description:
      'The risk engine whose signals drive these detections.',
    href: '/sign/security-score',
  },
  {
    label: 'Use case: identity security posture',
    description:
      'Posture, risk, and threats as one weekly program.',
    href: '/solutions/identity-security-posture',
  },
  {
    label: 'What is ITDR?',
    description:
      'Identity threat detection and response, explained.',
    href: '/glossary/itdr',
  },
]

export default function ItdrPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Identity Threat Detection & Response (ITDR)"
        description="ITDR built into the identity provider: MITRE ATT&CK-mapped detections, automated response playbooks, bidirectional SSF/CAEP signals, and SIEM export, from the platform that owns the session."
        path="/sign/itdr"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · ITDR"
          title={
            <>
              Detect identity attacks
              <br />
              from inside the identity layer.
            </>
          }
          body="Attackers log in more often than they break in. Monosign detects identity threats at the authentication and session layer, responds automatically (revoke, disable, step-up), and exchanges risk signals with your ecosystem over SSF/CAEP."
          badges={[
            'MITRE ATT&CK-mapped detections',
            'SSF / CAEP signals, both directions',
            'Automated response playbooks',
          ]}
        />
        <SolutionPillars
          title={<>The IdP is the sensor, and the enforcer.</>}
          body="Bolt-on ITDR tools watch logs from the outside. Monosign owns the session, so detection and response happen in the same place, in the same second."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From signal to response in three steps.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
